/**
 * Mirrors PHP Model::calculate_mileage and Users::Read_Field_Engineer_Miles drive_time.
 * Miles use the same midpoint haversine approximation as the API.
 */

export function calculateMileage(latitude1, longitude1, latitude2, longitude2, noCeil = false) {
	if (
		latitude1 == null || longitude1 == null || latitude2 == null || longitude2 == null ||
		latitude1 === '' || longitude1 === '' || latitude2 === '' || longitude2 === ''
	) {
		return 0;
	}

	const lat1 = typeof latitude1 === 'string' ? parseFloat(latitude1) : latitude1;
	const lon1 = typeof longitude1 === 'string' ? parseFloat(longitude1) : longitude1;
	const lat2 = typeof latitude2 === 'string' ? parseFloat(latitude2) : latitude2;
	const lon2 = typeof longitude2 === 'string' ? parseFloat(longitude2) : longitude2;

	if ([lat1, lon1, lat2, lon2].some((n) => Number.isNaN(n))) {
		return 0;
	}

	const earthRadius = 3959;
	const latFrom = (lat1 * Math.PI) / 180;
	const lonFrom = (lon1 * Math.PI) / 180;
	const latTo = (lat2 * Math.PI) / 180;
	const lonTo = (lon2 * Math.PI) / 180;

	const midpointLat = latFrom;
	const midpointLon = lonTo;

	const toLatDelta = latTo - midpointLat;
	const toLonDelta = lonTo - midpointLon;
	const fromLatDelta = latFrom - midpointLat;
	const fromLonDelta = lonFrom - midpointLon;

	let miles = 0;
	let angle = 2 * Math.asin(Math.sqrt(
		Math.pow(Math.sin(toLatDelta / 2), 2) +
		Math.cos(latFrom) * Math.cos(latTo) * Math.pow(Math.sin(toLonDelta / 2), 2)
	));
	miles += angle * earthRadius;

	angle = 2 * Math.asin(Math.sqrt(
		Math.pow(Math.sin(fromLatDelta / 2), 2) +
		Math.cos(latFrom) * Math.cos(latTo) * Math.pow(Math.sin(fromLonDelta / 2), 2)
	));
	miles += angle * earthRadius;

	if (noCeil) {
		return Math.round(miles * 100) / 100;
	}
	return Math.ceil(miles);
}

/** Same formula as Users.php: ceil(round(miles * 1.25 * 1.33, 2)) */
export function calculateDriveTimeMinutes(miles) {
	return Math.ceil(Math.round(miles * 1.25 * 1.33 * 100) / 100);
}

export function driveTimeForLeg(from, to) {
	const miles = calculateMileage(from.lat, from.lng, to.lat, to.lng);
	return {
		miles,
		drive_time: calculateDriveTimeMinutes(miles),
	};
}

export function legMidpoint(from, to) {
	return {
		lat: (from.lat + to.lat) / 2,
		lng: (from.lng + to.lng) / 2,
	};
}

/** Offset a point perpendicular to the leg so labels sit beside the line, not on it. */
export function offsetLegLabelPosition(from, to, offsetMeters = 180) {
	const mid = legMidpoint(from, to);
	const dLat = to.lat - from.lat;
	const dLng = to.lng - from.lng;
	const length = Math.sqrt((dLat * dLat) + (dLng * dLng));
	if (length === 0) {
		return mid;
	}

	const perpLat = -dLng / length;
	const perpLng = dLat / length;
	const offsetDeg = offsetMeters / 111320;

	return {
		lat: mid.lat + (perpLat * offsetDeg),
		lng: mid.lng + (perpLng * offsetDeg),
	};
}

/**
 * Position drive-time labels away from stop pins - biased toward leg start
 * with extra clearance on short legs.
 */
export function driveTimeLabelPosition(from, to) {
	const dLat = to.lat - from.lat;
	const dLng = to.lng - from.lng;
	const length = Math.sqrt((dLat * dLat) + (dLng * dLng));
	if (length === 0) {
		return legMidpoint(from, to);
	}

	// Sit ~35% along the leg from the start, away from the destination stop marker.
	const along = {
		lat: from.lat + (dLat * 0.35),
		lng: from.lng + (dLng * 0.35),
	};

	const legMeters = length * 111320;
	// Short legs need a larger perpendicular push so labels don't sit under stop pins.
	const offsetMeters = Math.max(240, 200 + Math.max(0, 800 - legMeters) * 0.15);

	const perpLat = -dLng / length;
	const perpLng = dLat / length;
	const offsetDeg = offsetMeters / 111320;

	return {
		lat: along.lat + (perpLat * offsetDeg),
		lng: along.lng + (perpLng * offsetDeg),
	};
}
